import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Modal, Spin, Drawer } from "antd";
import { motion } from "framer-motion";
import Slider from "react-slick";
import { FaStar, FaFilter } from "react-icons/fa";
import { ShieldCheck, Ruler, Truck, Award } from 'lucide-react'; // Optional: Install lucide-react or use emojis
import Hero from "../../common/Hero";
import Navbar from "../../common/Navbar";
import Footer from "../../common/Footer";
import BlogImage from "../../assests/images/hero.jpg";
import VImage from "../../assests/images/v.jpg";
import RImage from "../../assests/images/r.jpg";

// --- GLOBAL CONFIGURATION ---
const CATEGORY_ORDER = ['Blinds', 'Carpets', 'Furniture', 'Sofa Collections'];

// Helper function to group services by category
const groupServicesByCategory = (services) => {
  return services.reduce((acc, service) => {
    const categoryName = service.category;
    if (categoryName && CATEGORY_ORDER.includes(categoryName)) {
      if (!acc[categoryName]) {
        acc[categoryName] = [];
      }
      acc[categoryName].push(service);
    }
    return acc;
  }, {});
};

function Ourcleaning() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({ category: [], subcategory: [] });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState("");
  const [providerName, setProviderName] = useState({ firstName: "", lastName: "" });

  // Track visible card index per category for slide functionality
  const [visibleIndex, setVisibleIndex] = useState({});

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE;

  // Google Fonts
  useEffect(() => {
    const id = "ourcleaning-google-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700&family=Playfair+Display:wght@400;600;700&display=swap";
      document.head.appendChild(link);
    }
    document.documentElement.style.setProperty("--font-heading", "'Playfair Display', serif");
    document.documentElement.style.setProperty("--font-body", "'Inter', system-ui, sans-serif");
  }, []);

  // Load user info
  useEffect(() => {
    const auth = localStorage.getItem("auth");
    if (auth) {
      const user = JSON.parse(auth)?.user;
      if (user) {
        setProviderName({ firstName: user.firstName, lastName: user.lastName });
        setShowProviderModal(user.role || "");
      }
    }
  }, []);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/service/readAllService`);
        const data = Array.isArray(res.data.data.services) ? res.data.data.services : [];
        setServices(data);
        const uniqueCategories = [...new Set(data.map(s => s.category).filter(c => CATEGORY_ORDER.includes(c)))];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error("Failed to load services:", err);
        setError("Failed to load services. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [API_BASE]);

  // Highlight search
  const highlightText = (text = "", search) => {
    if (!search) return text;
    try {
      const regex = new RegExp(`(${search})`, "gi");
      return String(text).replace(regex, `<span class="bg-yellow-200 px-1 rounded">$1</span>`);
    } catch {
      return text;
    }
  };

  // Filtered services
  const filteredServices = services.filter(s => {
    const name = s.name?.toLowerCase() || "";
    const desc = s.description?.toLowerCase() || "";
    const cat = s.category || "";
    const sub = s.subcategory || "";

    const matchesSearch = !searchTerm || name.includes(searchTerm.toLowerCase()) || desc.includes(searchTerm.toLowerCase());
    const matchesCat = selectedFilters.category.length === 0 || selectedFilters.category.includes(cat);
    const matchesSub = selectedFilters.subcategory.length === 0 || selectedFilters.subcategory.includes(sub);

    return matchesSearch && matchesCat && matchesSub;
  });

  const groupedServices = groupServicesByCategory(filteredServices);

  // Slide functions
  const showNext = (categoryName, total) => {
    const current = visibleIndex[categoryName] || 0;
    setVisibleIndex(prev => ({
      ...prev,
      [categoryName]: current + 1 >= total ? 0 : current + 1
    }));
  };

  const showPrev = (categoryName, total) => {
    const current = visibleIndex[categoryName] || 0;
    setVisibleIndex(prev => ({
      ...prev,
      [categoryName]: current - 1 < 0 ? total - 1 : current - 1
    }));
  };

  // ================= AUTO SLIDER =================
  useEffect(() => {
    if (!groupedServices) return;

    const interval = setInterval(() => {
      setVisibleIndex(prev => {
        const newIndex = { ...prev };

        CATEGORY_ORDER.forEach(categoryName => {
          const servicesInSection = groupedServices[categoryName];
          if (!servicesInSection) return;

          const current = prev[categoryName] || 0;
          const total = servicesInSection.length;

          if (current + 4 >= total) {
            newIndex[categoryName] = 0;
          } else {
            newIndex[categoryName] = current + 1;
          }
        });

        return newIndex;
      });
    }, 6000); // 6 seconds auto slide

    return () => clearInterval(interval);
  }, [groupedServices]);

  // Filter component
  const FilterContent = ({ selectedFilters, setSelectedFilters }) => {
    const handleFilterChange = (filterKey, value, isChecked) => {
      setSelectedFilters(prev => {
        const currentSelections = prev[filterKey] || [];
        const newSelections = isChecked
          ? [...new Set([...currentSelections, value])]
          : currentSelections.filter(item => item !== value);
        return { ...prev, [filterKey]: newSelections };
      });
    };

    const getSubcategories = (cat) =>
      [...new Set(services.filter(s => s.category === cat).map(s => s.subcategory).filter(Boolean))];

    const isCatChecked = (cat) => selectedFilters.category.includes(cat);
    const isSubChecked = (sub) => selectedFilters.subcategory.includes(sub);

    return (
      <div className="p-6 bg-white rounded-2xl shadow-lg font-body">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-green-700 font-heading">Filter Services</h3>
          <button
            onClick={() => setSelectedFilters({ category: [], subcategory: [] })}
            className="text-red-600 hover:text-red-800 text-sm font-semibold"
          >
            Clear All
          </button>
        </div>

        {/* Categories */}
        <div className="mb-6">
          <h4 className="font-bold text-gray-800 text-lg mb-3 border-b pb-2">Categories</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {categories.map((cat) => (
              <label
                key={cat}
                htmlFor={`cat-${cat}`}
                className="flex items-center p-2 rounded-lg hover:bg-green-50 cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  id={`cat-${cat}`}
                  checked={isCatChecked(cat)}
                  onChange={(e) => handleFilterChange("category", cat, e.target.checked)}
                  className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="ml-3 font-medium text-gray-700">{cat}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Subcategories */}
        {selectedFilters.category.length > 0 && (
          <div className="mb-4">
            <h4 className="font-bold text-gray-800 text-lg mb-3 border-b pb-2">Subcategories</h4>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {categories.filter(isCatChecked).map((cat) => (
                <div key={cat}>
                  <h5 className="font-semibold text-green-700 text-sm mb-2">{cat}</h5>
                  <div className="space-y-1 pl-3">
                    {getSubcategories(cat).map((sub) => (
                      <label
                        key={sub}
                        htmlFor={`sub-${sub}`}
                        className="flex items-center p-1 rounded hover:bg-green-50 cursor-pointer transition"
                      >
                        <input
                          type="checkbox"
                          id={`sub-${sub}`}
                          checked={isSubChecked(sub)}
                          onChange={(e) =>
                            handleFilterChange("subcategory", sub, e.target.checked)
                          }
                          className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="ml-2 text-gray-600 text-sm">{sub}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    );
  };

  // Preview Modal
  const openModal = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setSelectedService(null);
    setIsModalOpen(false);
  };

  // Testimonials & Service Areas
  const testimonials = [
    { name: "Aisha", feedback: "Outstanding service! Cleaners were on time and thorough.", img: "https://randomuser.me/api/portraits/women/68.jpg", rating: 5 },
    { name: "Ali", feedback: "They made my office spotless. Highly recommended!", img: "https://randomuser.me/api/portraits/men/76.jpg", rating: 4 },
    { name: "Fatima", feedback: "Great attention to detail. Will book again!", img: "https://randomuser.me/api/portraits/women/45.jpg", rating: 5 },
  ];

  const serviceArea = [
    "Hayes", "Hunslow", "Southall", "Uxbridge", "Greenford", "Northolt",
    "Windsor", "Ruislip", "Harrow", "Maidenhead", "High Wycombe"
  ];

  const sliderSettings = {
    dots: true,
    infinite: true,
    autoplay: true,
    speed: 800,
    slidesToShow: 2,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
      { breakpoint: 480, settings: { slidesToShow: 1, dots: false } }
    ],
  };

  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    {
      q: "Do you offer free home measurements?",
      a: "Yes! We provide a completely free, no-obligation home survey across Greater London. We bring samples directly to you so you can see how they look in your space."
    },
    {
      q: "How long does the installation take?",
      a: "Most blind and carpet installations are completed within 2–4 hours, depending on the number of rooms. We always ensure a clean and tidy finish before leaving."
    },
    {
      q: "What areas do you cover?",
      a: "We serve Greenford, Northolt, Windsor, Ruislip, Harrow, Maidenhead, and the wider Greater London area."
    }
  ];


  const features = [
    {
      title: "Bespoke Fitting",
      desc: "Every blind and carpet is measured to the millimeter for a perfect finish.",
      icon: <Ruler className="w-8 h-8 text-[#69A105]" />
    },
    {
      title: "Premium Materials",
      desc: "We source high-durability fabrics and carpets that stand the test of time.",
      icon: <Award className="w-8 h-8 text-[#69A105]" />
    },
    {
      title: "Expert Installation",
      desc: "Professional, mess-free fitting by our London-based specialist team.",
      icon: <Truck className="w-8 h-8 text-[#69A105]" />
    },
    {
      title: "12-Month Warranty",
      desc: "Peace of mind guaranteed with our comprehensive after-care support.",
      icon: <ShieldCheck className="w-8 h-8 text-[#69A105]" />
    }
  ];
  // --------------------- Render ---------------------
  return (
    <>
      {/* Provider Welcome Modal */}
      <Modal open={Boolean(showProviderModal)} onCancel={() => setShowProviderModal("")} footer={null} centered>
        <div className="text-center py-6 font-body">
          {showProviderModal === "provider" && (
            <>
              <h2 className="text-2xl font-semibold mb-2 font-heading text-green-700">Welcome Dear Employee!</h2>
              <p className="text-lg">Mr. {providerName.firstName} {providerName.lastName}, check your profile for orders.</p>
              <button onClick={() => navigate("/Profile")} className="mt-6 w-60 py-2 rounded text-white font-semibold bg-green-700">Go to Profile</button>
            </>
          )}
          {showProviderModal === "user" && (
            <>
              <h2 className="text-2xl font-semibold mb-2 font-heading text-green-700">Welcome Dear Customer!</h2>
              <p className="text-lg">{providerName.firstName} {providerName.lastName}, view your order history in your profile.</p>
              <button onClick={() => navigate("/Profile")} className="mt-6 w-60 py-2 rounded text-white font-semibold bg-green-700">Go to Profile</button>
            </>
          )}
          {showProviderModal === "admin" && (
            <>
              <h2 className="text-2xl font-semibold mb-2 font-heading text-green-700">Hello Admin!</h2>
              <p className="text-lg">{providerName.firstName} {providerName.lastName}, open your dashboard below.</p>
              <button onClick={() => navigate("/admin")} className="mt-6 w-60 py-2 rounded text-white font-semibold bg-green-700">Go to Admin Dashboard</button>
            </>
          )}
        </div>
      </Modal>

   
      {/* Premium Product Style Modal */}

      <Modal
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        centered
        width={1100}
        className="rounded-2xl"
        bodyStyle={{ padding: 0 }}
      >

        {selectedService && (
          <div className="grid md:grid-cols-2 font-body">

            {/* LEFT IMAGE SECTION */}
            <div className="relative bg-gray-50 p-6 flex items-center justify-center">
              <img
                src={selectedService.image || "https://source.unsplash.com/600x600/?interior"}
                alt={selectedService.name}
                className="w-full h-[500px] object-cover rounded-xl shadow-lg"
              />
            </div>

            {/* RIGHT DETAILS SECTION */}
            <div className="p-8 space-y-5">

              {/* Title */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {selectedService.name}
                </h2>

                <p className="text-green-700 font-semibold mt-2">
                  {selectedService.category}
                </p>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed">
                {selectedService.description}
              </p>

              {/* Price (If Available) */}
              {selectedService.price && (
                <div>
                  <p className="text-2xl font-bold text-green-700">
                    £ {selectedService.price}
                  </p>
                </div>
              )}

              {/* Subcategory */}
              {selectedService.subcategory && (
                <div className="flex gap-3 flex-wrap">
                  <span className="px-4 py-2 bg-gray-100 rounded-full text-sm">
                    {selectedService.subcategory}
                  </span>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="pt-6 space-y-3">

                <button
                  onClick={() => navigate("/register")}
                  className="w-full bg-green-700 text-white py-4 rounded-xl font-semibold hover:bg-green-800 transition"
                >
                  Book Installation Service
                </button>

                <button
                  onClick={closeModal}
                  className="w-full border border-gray-300 py-4 rounded-xl hover:bg-gray-50 transition"
                >
                  Close
                </button>

              </div>

            </div>

          </div>
        )}
</Modal>

      {/* Navbar */}
      <div className="bg-white shadow-sm">
        <Navbar />
      </div>

      <>
        {/* ================= HERO ================= */}
        <Hero text="Installation Services" text1="Blind • Carpet • Furniture " image={BlogImage} />


        {/* Blinds Showcase Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Premium Blinds Collection
              </h2>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                Explore our stylish, modern, and high-quality blinds designed to
                transform your home with elegance and functionality.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-8 items-center justify-center">

              {/* Image 1 */}
              <div className="relative w-full md:w-1/2 overflow-hidden rounded-2xl shadow-xl group">
                <img
                  src={RImage}
                  alt="Modern Roller Blinds"
                  className="w-full h-[450px] object-cover transition duration-700 group-hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-6">
                  <h3 className="text-xl font-semibold">Roller Blinds</h3>
                  <p className="text-sm mt-2">Sleek & modern window solutions.</p>
                </div>
              </div>

              {/* Image 2 */}
              <div className="relative w-full md:w-1/2 overflow-hidden rounded-2xl shadow-xl group">
                <img
                  src={VImage}
                  alt="Elegant Venetian Blinds"
                  className="w-full h-[450px] object-cover transition duration-700 group-hover:scale-110"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-6">
                  <h3 className="text-xl font-semibold">Venetian Blinds</h3>
                  <p className="text-sm mt-2">Classic style with adjustable light control.</p>
                </div>
              </div>

            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <Link to="/register">
                <button className="bg-lime-600 hover:bg-lime-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-lime-600/30 transition-all duration-300">
                  Shop All Blinds

                </button>
              </Link>
            </div>

          </div>
        </section>

        {/* ================= SEO STORY INTRO (H1) ================= */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold text-center mb-10 text-green-700"
              style={{ fontFamily: "'Playfair Display', serif" }}>

              Expert Blind, Carpet & Furniture Installation Services
            </h1>

            <p className="text-lg text-gray-600 leading-relaxed mb-6">
              At <span className="text-green-700 font-semibold">MF Blinds</span>,
              we don’t just install products — we transform living spaces.
              Our professional blind installation, carpet fitting, and furniture
              assembly services are designed to deliver comfort, style, and durability.
            </p>

            <p className="text-lg text-gray-600 leading-relaxed">
              Whether you are renovating, upgrading, or furnishing a new property,
              our experienced installers ensure precision measurements, secure
              fittings, and flawless finishing.
            </p>
          </div>
        </section>

        {/* ================= SERVICES SECTION ================= */}
        <section className=" px-4 md:px-8 bg-white font-body text-gray-900">
          <div className="max-w-7xl mx-auto">

            <h2 className="text-5xl font-bold text-center mb-10 text-green-700"
              style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Professional Installation Services
            </h2>

            <div className="flex flex-col md:flex-row gap-8">

              {/* Sidebar Desktop */}
              <aside className="hidden md:block md:w-1/3 lg:w-1/4">
                <div className="rounded-xl p-5 bg-gray-100 border border-gray-200 shadow-sm">
                  <FilterContent
                    selectedFilters={selectedFilters}
                    setSelectedFilters={setSelectedFilters}
                  />
                </div>
              </aside>

              {/* Mobile Filter Button */}
              <div className="md:hidden mb-4 flex justify-center">
                <button
                  onClick={() => setShowFilterDrawer(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-semibold bg-green-700"
                >
                  <FaFilter /> Filter
                </button>
              </div>

              {/* Mobile Drawer */}
              <Drawer
                title="Filter Services"
                placement="left"
                onClose={() => setShowFilterDrawer(false)}
                open={showFilterDrawer}
              >
                <FilterContent
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              </Drawer>

              {/* Services Grid */}
              <div className="flex-1">

                {/* Search */}
                <div className="mb-6">
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search: Blinds • Carpets • Furniture • Sofa Collections"
                    className="w-full px-4 py-3 rounded-md border text-sm sm:text-base"
                  />
                </div>

                {loading ? (
                  <div className="flex justify-center items-center py-20">
                    <div className="flex flex-col justify-center items-center py-32 space-y-4">
                      <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-gray-500 animate-pulse">
                        Loading premium services...
                      </p>
                    </div>
                  </div>
                ) : error ? (
                  <p className="text-center text-red-500">{error}</p>
                ) : (
                  <div className="space-y-12">
                    {CATEGORY_ORDER.map((categoryName) => {
                      const servicesInSection = groupedServices[categoryName];
                      if (!servicesInSection || servicesInSection.length === 0) return null;

                      const start = visibleIndex[categoryName] || 0;
                      const end = start + 4;

                      return (
                        <div key={categoryName}>
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-2xl font-bold text-green-700">
                              {categoryName}
                            </h3>
                            <div className="flex gap-2">
                              <button onClick={() => showPrev(categoryName)} className="p-2 rounded-full bg-gray-200">
                                ←
                              </button>
                              <button onClick={() => showNext(categoryName, servicesInSection.length)} className="p-2 rounded-full bg-gray-200">
                                →
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {servicesInSection.slice(start, end).map((service, i) => (
                              <motion.div
                                key={service.name + i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: i * 0.05 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl shadow-md overflow-hidden group cursor-pointer"
                                onClick={() => openModal(service)}>
                                <div className="relative">
                                  <img
                                    src={service.image || "https://source.unsplash.com/400x300/?interior"}
                                    alt={service.name}
                                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                                  />
                                </div>

                                <div className="p-4">
                                  <h4 className="font-bold text-lg text-gray-800">
                                    {service.name}
                                  </h4>
                                  {service.description && (
                                    <p className="text-gray-600 text-sm mt-2 line-clamp-3">
                                      {service.description}
                                    </p>
                                  )}
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

            </div>
          </div>
        </section>

        {/* ================= PROCESS STORY SECTION ================= */}
       <section className="py-20 bg-gray-50">
  <div className="max-w-6xl mx-auto px-4 text-center">
    <h2 className="text-4xl font-bold text-gray-900 mb-14">
      Our Professional Installation Process
    </h2>

    <div className="grid md:grid-cols-3 gap-8">
      
      {/* Card 1 */}
      <div className="bg-white rounded-2xl shadow-md p-8 text-left hover:shadow-xl transition duration-300 border border-gray-100">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-lime-600 text-white text-xl font-bold mb-6">
          1
        </div>
        <h3 className="text-xl font-semibold mb-3 text-gray-900">
          Consultation & Measurement
        </h3>
        <p className="text-gray-600 leading-relaxed">
          We carefully assess your space and provide expert recommendations to ensure perfect sizing and style selection.
        </p>
      </div>

      {/* Card 2 */}
      <div className="bg-white rounded-2xl shadow-md p-8 text-left hover:shadow-xl transition duration-300 border border-gray-100">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-lime-600 text-white text-xl font-bold mb-6">
          2
        </div>
        <h3 className="text-xl font-semibold mb-3 text-gray-900">
          Precision Installation
        </h3>
        <p className="text-gray-600 leading-relaxed">
          Our professional installers ensure secure, safe, and flawless fitting with attention to every detail.
        </p>
      </div>

      {/* Card 3 */}
      <div className="bg-white rounded-2xl shadow-md p-8 text-left hover:shadow-xl transition duration-300 border border-gray-100">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-lime-600 text-white text-xl font-bold mb-6">
          3
        </div>
        <h3 className="text-xl font-semibold mb-3 text-gray-900">
          Final Quality Inspection
        </h3>
        <p className="text-gray-600 leading-relaxed">
          We thoroughly inspect everything to guarantee long-term performance, safety, and customer satisfaction.
        </p>
      </div>

    </div>
  </div>
</section>

        {/* ================= CTA ================= */}
        <section className="py-20 bg-green-700 text-white text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-lg mb-8">
            Book your free consultation today and let our experts handle your installation with perfection.
          </p>
          <Link to="/register">
            <button className="bg-white text-green-700 font-semibold px-8 py-4 rounded-full shadow-md hover:shadow-lg transition">
              Book Your Free Consultation
            </button>
          </Link>
        </section>

        {/* ================= TESTIMONIALS ================= */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2
              className="text-4xl md:text-5xl font-bold mb-12 text-center text-green-700"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              What Our Clients Say About Our Installation Services
            </h2>

            <Slider {...sliderSettings}>
              {testimonials.map((t, i) => (
                <div key={i} className="px-4">
                  <div className="bg-white rounded-xl shadow-md p-8 text-center hover:shadow-lg transition-shadow duration-300">
                    <img
                      src={t.img}
                      alt={t.name}
                      className="w-24 h-24 mx-auto rounded-full mb-4 object-cover"
                    />

                    <h3 className="font-semibold text-lg mb-2">{t.name}</h3>

                    <p className="text-gray-600 mb-4 italic">
                      "{t.feedback}"
                    </p>

                    <div className="flex justify-center gap-1">
                      {[...Array(5)].map((_, idx) => (
                        <FaStar
                          key={idx}
                          className={`h-4 w-4 ${idx < t.rating ? "text-yellow-400" : "text-gray-300"
                            }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        </section>

        {/* ================= FAQ SECTION ================= */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
              Frequently Asked Questions About Blind, Carpet & Furniture Installation
            </h2>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === index ? null : index)
                    }
                    className="w-full flex justify-between items-center p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-800">
                      {faq.q}
                    </span>

                    <span
                      className={`transform transition-transform ${openIndex === index ? "rotate-180" : ""
                        }`}
                    >
                      ▼
                    </span>
                  </button>

                  {openIndex === index && (
                    <div className="p-5 bg-gray-50 text-gray-600 border-t border-gray-200 animate-fadeIn">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= FEATURES SECTION ================= */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Choose MF Blinds for Installation Services?
              </h2>
              <div className="w-24 h-1 bg-green-600 mx-auto rounded-full"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((f, i) => (
                <div
                  key={i}
                  className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
                >
                  <div className="mb-4 text-green-600 text-3xl">
                    {f.icon}
                  </div>

                  <h3 className="text-xl font-semibold mb-3 text-gray-800">
                    {f.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* ================= FOOTER ================= */}
        <Footer />
      </>
    </>
  );
}

export default Ourcleaning;
